import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { TabView, TabPanel } from 'primereact/tabview';
import { Toast } from 'primereact/toast';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { usePermissions } from "../../../../hooks/usePermissions";
import { selectUserParams } from '../../../../store/slices/userParamsSlice';

// Components
import DealerInvoiceTable from './components/DealerInvoiceTable';
import DealerInvoiceFilters from './components/DealerInvoiceFilters';
import DealerInvoiceDialog from './components/DealerInvoiceDialog';
import DealerInvoiceHistoryDialog from './components/DealerInvoiceHistoryDialog';
import ClaimsDetailDialog from './components/ClaimsDetailDialog';

// Hooks
import { useDealerInvoiceList } from './hooks/useDealerInvoiceList';
import { useDealerInvoiceOperations } from './hooks/useDealerInvoiceOperations';

import { InvoiceStatus } from './constants/invoiceStatus';
import { TAB_IDS } from './constants/invoiceTabs';

const NO_TRANSLATION = 'NOTRANSLATION';

const DealerInvoiceEntryPage = () => {
    const { th, getCurrentLanguageId } = useLanguage();

    const {
        hasWarrantyTeamRole, hasWarrantyManagerRole, hasAdminRole,
        hasDealerRole
    } = usePermissions();
    const userParams = useSelector(selectUserParams);

    const isWarrantyTeam = hasWarrantyTeamRole() || hasWarrantyManagerRole() || hasAdminRole();
    const isDealer = hasDealerRole();

    const isDomesticDealer = isDealer && userParams?.isDomestic === true;

    const toast = useRef(null);

    const tabs = useMemo(() => {
        const t = (key) => {
            const value = th(key);
            return !value || value === NO_TRANSLATION ? null : value;
        };

        const tabLabel = (key, fallback) =>
            (isWarrantyTeam ? t(`warrantyTeam.tabs.${key}`) : null)
            ?? t(`dealerInvoice.tabs.${key}`)
            ?? fallback;

        return [
            {
                id: TAB_IDS.SAP_RECORDS,
                status: null,
                label: (isWarrantyTeam ? t('warrantyTeam.tabs.unprocessedSap') : null)
                    ?? t('dealerInvoice.tabs.sapRecords')
                    ?? 'Bayi Fatura Girişi Bekleyen',
                icon: 'pi pi-database',
                visible: true
            },
            {
                id: TAB_IDS.DRAFT,
                status: InvoiceStatus.DRAFT,
                label: th('dealerInvoice.tabs.draft'),
                icon: 'pi pi-file-edit',
                visible: !isWarrantyTeam
            },
            {
                id: TAB_IDS.PENDING,
                status: InvoiceStatus.PENDING,
                label: tabLabel('pending', 'Onay Bekleyen Faturalar'),
                icon: 'pi pi-clock',
                visible: isWarrantyTeam || !isDomesticDealer
            },
            {
                id: TAB_IDS.RETURNED,
                status: InvoiceStatus.RETURNED,
                label: tabLabel('returned', 'İade Edilenler'),
                icon: 'pi pi-replay',
                visible: true
            },
            {
                id: TAB_IDS.REJECTED,
                status: InvoiceStatus.REJECTED,
                label: tabLabel('rejected', 'Reddedilenler'),
                icon: 'pi pi-times-circle',
                visible: true
            },
            {
                id: TAB_IDS.APPROVED,
                status: InvoiceStatus.APPROVED,
                label: tabLabel('approved', 'Onaylananlar'),
                icon: 'pi pi-check-circle',
                visible: true
            },
            {
                id: TAB_IDS.ALL_GT_ORDERS,
                status: null,
                label: t('warrantyTeam.tabs.allGTOrders') ?? 'Tüm GT Siparişleri',
                icon: 'pi pi-th-large',
                visible: isWarrantyTeam
            }
        ].filter(tab => tab.visible);
    }, [th, isWarrantyTeam, isDomesticDealer]);

    const [activeIndex, setActiveIndex] = useState(() =>
        Math.max(0, tabs.findIndex(tab => tab.id === (isWarrantyTeam ? TAB_IDS.PENDING : TAB_IDS.SAP_RECORDS)))
    );

    const activeTab = tabs[activeIndex] ?? tabs[0];
    const activeTabId = activeTab.id;
    const currentStatus = activeTab.status;

    const [historyDialogVisible, setHistoryDialogVisible] = useState(false);
    const [historyRecord, setHistoryRecord] = useState(null);

    const [claimsDetailDialogVisible, setClaimsDetailDialogVisible] = useState(false);
    const [selectedGroupIdForDetail, setSelectedGroupIdForDetail] = useState(null);

    // Listeleme Hook'u
    const {
        items, totalRecords, loading, lazyParams, onPage, onSort,
        filterFields, handleApplyFilters, handleResetFilters, refetch,
        apiFilters
    } = useDealerInvoiceList(th, activeTabId, currentStatus, isWarrantyTeam, isDomesticDealer ? 'DOMESTIC' : 'INTERNATIONAL');

    // Operasyon Hook'u
    const {
        dialogVisible, openNew, openEdit, hideDialog, formData,
        onInputChange, onDateChange, onTextareaChange, submitted,
        handleSubmit, handleSaveDraft, handleSubmitDraft, validateForSend,
        handleApprove, handleReject, handleReturn,
        isSubmitting, isSavingDraft, isSending, sendAttempted,
        isEditMode, isLoadingDetail, newFiles,
        onFilesChange, existingDocuments, onDeleteDocument,
        onDownloadDocument, setToastCallback
    } = useDealerInvoiceOperations(refetch, th, isDomesticDealer, isWarrantyTeam);

    useEffect(() => {
        setToastCallback(toast);
    }, [setToastCallback]);

    useEffect(() => {
        refetch();
    }, [activeIndex, refetch]);

    useEffect(() => {
        if (activeIndex >= tabs.length) {
            setActiveIndex(0);
        }
    }, [activeIndex, tabs.length]);

    const handleViewDetail = (rowData) => {
        const groupId = rowData.groupId || rowData.vbeln;
        if (groupId) {
            setSelectedGroupIdForDetail(groupId);
            setClaimsDetailDialogVisible(true);
        }
    };

    const exportParams = useMemo(() => {
        let mode;
        if (isWarrantyTeam) {
            if (activeTabId === TAB_IDS.SAP_RECORDS) mode = 1;       // WarrantyTeamAvailableSap
            else if (activeTabId === TAB_IDS.ALL_GT_ORDERS) mode = 4;  // AllGTOrders
            else mode = 3;                                             // WarrantyTeamApprovals
        } else {
            if (activeTabId === TAB_IDS.SAP_RECORDS) mode = 0;       // DealerAvailableSap
            else mode = 2;                                             // DealerSubmissions
        }

        const formattedFilters = {};
        if (apiFilters) {
            Object.entries(apiFilters).forEach(([key, value]) => {
                if (value === null || value === undefined || value === '') return;
                const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
                formattedFilters[pascalKey] = String(value);
            });
        }

        const params = {
            Mode: mode,
            LanguageId: getCurrentLanguageId(),
            ...formattedFilters
        };

        if (currentStatus !== null && currentStatus !== undefined) {
            params.Status = currentStatus;
        }

        return params;
    }, [isWarrantyTeam, activeTabId, currentStatus, apiFilters, getCurrentLanguageId]);

    return (
        <div className="card">
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="page-header mb-4">
                <h1 className="page-title mb-2">
                    {isWarrantyTeam
                        ? th('warrantyTeam.pageTitle')
                        : th('dealerInvoice.pageTitle')}
                </h1>
                <p className="text-gray-600">
                    {isWarrantyTeam
                        ? th('warrantyTeam.listDescription')
                        : th('dealerInvoice.listDescription')}
                </p>
            </div>

            <DealerInvoiceFilters
                filterFields={filterFields}
                handleApplyFilters={handleApplyFilters}
                handleResetFilters={handleResetFilters}
                activeTabId={activeTabId}
            />

            <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
                {tabs.map((tab) => (
                    <TabPanel key={tab.id} header={<span><i className={`${tab.icon} mr-2`}></i>{tab.label}</span>}>
                        <DealerInvoiceTable
                            items={items}
                            loading={loading}
                            totalRecords={totalRecords}
                            lazyParams={lazyParams}
                            onPage={onPage}
                            onSort={onSort}
                            activeTabId={activeTabId}
                            // Actions
                            handleCreate={openNew}
                            handleEdit={openEdit}
                            handleShowHistory={(r) => {
                                setHistoryRecord(r);
                                setHistoryDialogVisible(true);
                            }}
                            handleApprove={handleApprove}
                            handleReject={handleReject}
                            handleReturn={handleReturn}
                            handleViewDetail={handleViewDetail}
                            isWarrantyTeam={isWarrantyTeam}
                            isDomesticDealer={isDomesticDealer}
                            th={th}
                            // Export
                            exportParams={exportParams}
                        />
                    </TabPanel>
                ))}
            </TabView>

            <DealerInvoiceDialog
                visible={dialogVisible}
                onHide={hideDialog}
                formData={formData}
                onInputChange={onInputChange}
                onDateChange={onDateChange}
                onTextareaChange={onTextareaChange}
                onSubmit={handleSubmit}
                onSaveDraft={handleSaveDraft}
                onSubmitDraft={handleSubmitDraft}
                validateForSend={validateForSend}
                submitted={submitted}
                sendAttempted={sendAttempted}
                isSubmitting={isSubmitting}
                isSavingDraft={isSavingDraft}
                isSending={isSending}
                isEditMode={isEditMode}
                isLoadingDetail={isLoadingDetail}
                newFiles={newFiles}
                onFilesChange={onFilesChange}
                existingDocuments={existingDocuments}
                onDeleteDocument={onDeleteDocument}
                onDownloadDocument={onDownloadDocument}
                isWarrantyTeam={isWarrantyTeam}
                isDomesticDealer={isDomesticDealer}
                th={th}
            />

            <DealerInvoiceHistoryDialog
                visible={historyDialogVisible}
                onHide={() => setHistoryDialogVisible(false)}
                record={historyRecord}
                th={th}
            />

            <ClaimsDetailDialog
                visible={claimsDetailDialogVisible}
                onHide={() => {
                    setClaimsDetailDialogVisible(false);
                    setSelectedGroupIdForDetail(null);
                }}
                groupId={selectedGroupIdForDetail}
                th={th}
            />
        </div>
    );
};

export default DealerInvoiceEntryPage;
